import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import RegisterVisitor from './pages/RegisterVisitor';
import VisitorLogs from './pages/VisitorLogs';
import Employees from './pages/Employees';
import VisitRequests from './pages/VisitRequests';
import Configuration from './pages/Configuration';
import EditProfile from './pages/EditProfile';
import SystemSettings from './pages/SystemSettings';
import EmailMaster from './pages/EmailMaster';
import Departments from './pages/Departments';
import Branches from './pages/Branches';
import SubDepartments from './pages/SubDepartments';
import EmployeeTypes from './pages/EmployeeTypes';
import Companies from './pages/Companies';
import { clearStoredAuth, getStoredAuthToken } from './utils/authToken';

function App() {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(() => !!getStoredAuthToken());

  React.useEffect(() => {
    const handleStorageChange = () => {
      const hasValidToken = !!getStoredAuthToken();

      if (!hasValidToken) {
        clearStoredAuth();
      }

      setIsAuthenticated(hasValidToken);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-change', handleStorageChange);

    handleStorageChange();

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-change', handleStorageChange);
    };
  }, []);

  const getRedirectPath = React.useMemo(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user && user.role && user.role.toLowerCase() === 'superadmin') {
          return '/companies';
        }
      }
    } catch (err) {
      console.error('Error parsing user JSON from localStorage:', err);
    }
    return '/dashboard';
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <Router>
        <Routes>
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/login" element={<Login onLoginSuccess={() => setIsAuthenticated(true)} />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <div className="app-shell flex h-screen overflow-hidden w-full font-sans text-[var(--wm-text)]">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <div className="app-frame flex flex-col flex-1 w-full overflow-hidden">
          <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
          <main className="app-main flex-1 overflow-x-hidden overflow-y-auto transition-all duration-200">
            <Routes>
              <Route path="/companies" element={<Companies />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/employees" element={<Employees />} />
              <Route path="/visit-requests" element={<VisitRequests />} />
              <Route path="/register" element={<RegisterVisitor />} />
              <Route path="/logs" element={<VisitorLogs />} />
              <Route path="/configuration" element={<Configuration />} />
              <Route path="/system-settings" element={<SystemSettings />} />
              <Route path="/email-master" element={<EmailMaster />} />
              <Route path="/departments" element={<Departments />} />
              <Route path="/branches" element={<Branches />} />
              <Route path="/sub-departments" element={<SubDepartments />} />
              <Route path="/employee-types" element={<EmployeeTypes />} />
              <Route path="/edit-profile" element={<EditProfile />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/" element={<Navigate to={getRedirectPath} replace />} />
              <Route path="*" element={<Navigate to={getRedirectPath} replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </Router>
  );
}

export default App;
